import { isValid, parse, parseISO } from 'date-fns';
import { useEffect, useRef, useState } from 'react';
import { formatUTCDate } from '../../../../../util';
import { DatePickerView } from '../types';

export interface UseDatePickerArgs {
  value?: string;
  onChange?: (dateISO: string | null) => void;
}

/**
 * Build a UTC-anchored Date at midnight for the given calendar y/m/d. Using UTC
 * everywhere internally means `getUTCDate()/getUTCMonth()/getUTCFullYear()`
 * always reflect the calendar date the user picked, regardless of the host
 * machine's timezone.
 */
const utcMidnight = (year: number, month: number, day: number) => new Date(Date.UTC(year, month, day));

/**
 * Parse a free-text date string (e.g. "Jun 27, 2026") into a UTC-anchored
 * midnight Date. Falls back to `null` for unparseable input.
 */
const parseUserInputToUTC = (input: string): Date | null => {
  if (!input) return null;
  // Try a couple of common formats first (typed by users), then a permissive fallback.
  const formats = ['MMM d, yyyy', 'MMM dd, yyyy', 'MMM d yyyy', 'MMM dd yyyy'];
  for (const fmt of formats) {
    const parsed = parse(input, fmt, new Date());
    if (isValid(parsed)) {
      return utcMidnight(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
    }
  }
  // Offset-free ISO date-times (e.g. "2026-06-27T00:00:00") — pin to the local
  // calendar day explicitly so we never depend on `new Date()`'s implicit
  // local-TZ interpretation drifting the calendar date around.
  const localDateTimePattern = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?$/;
  if (localDateTimePattern.test(input)) {
    const parsed = parseISO(input);
    if (!isValid(parsed)) return null;
    return utcMidnight(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
  }
  // ISO date-only (e.g. "2026-06-27") — new Date() treats these as UTC midnight,
  // so local getters would shift the calendar day west-of-UTC. Use UTC getters.
  const isoDateOnlyPattern = /^\d{4}-\d{2}-\d{2}$/;
  if (isoDateOnlyPattern.test(input)) {
    const parsed = new Date(input);
    if (!isValid(parsed)) return null;
    return utcMidnight(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate());
  }
  // Permissive fallback: let `new Date()` try, then re-anchor to UTC midnight using its local components.
  // Local getters (not UTC) — for free-text strings like "Feb 02, 2014" that JS
  // parses as *local* midnight, the local getters give back the calendar day
  // the author intended (using UTC getters would shift the day west-of-UTC).
  const fallback = new Date(input);
  if (!isValid(fallback)) return null;
  return utcMidnight(fallback.getFullYear(), fallback.getMonth(), fallback.getDate());
};

export const useDatePicker = ({ value, onChange }: UseDatePickerArgs) => {
  // `now` represents "today" as a UTC-anchored midnight derived from the
  // user's *local* calendar date, so today-highlighting in the calendar view
  // is independent of the host timezone.
  const today = new Date();
  const now = utcMidnight(today.getFullYear(), today.getMonth(), today.getDate());

  const [lookedMonth, setLookedMonth] = useState(now.getUTCMonth());
  const [lookedYear, setLookedYear] = useState(now.getUTCFullYear());
  const [chosenDate, setChosenDate] = useState<Date | null>(null);
  const [currentView, setCurrentView] = useState(DatePickerView.DatePicker);
  const [userInput, setUserInput] = useState<string>(formatUTCDate(value));
  // Track when userInput is updated as part of controlled value synchronization
  // to distinguish from user edits. Prevents the userInput effect from re-parsing
  // synchronized display text and overwriting chosenDate.
  const isSyncingRef = useRef(false);

  useEffect(() => {
    if (!value || !isValid(new Date(value))) {
      setChosenDate(null);
      setUserInput('');
      return;
    }
    // Incoming ISO values (e.g. "2026-06-27T00:00:00.000Z") are already UTC,
    // so we can use them directly. `getUTC*` reads will reflect the calendar
    // date encoded in the ISO string.
    const incoming = new Date(value);
    // Only refresh the visible input when it can't already re-parse to `value`
    // — otherwise the parent re-echoing our own onChange would clobber the
    // text the user just typed with our canonical `MMM dd yyyy` format.
    const parsedFromInput = parseUserInputToUTC(userInput);
    if (!parsedFromInput || parsedFromInput.getTime() !== incoming.getTime()) {
      // Set the sync flag only when the formatted value would actually change
      // userInput. A same-day controlled timestamp change (midnight → 14:30 on
      // Jun 15) produces the same 'Jun 15 2026' display string, so
      // `setUserInput` skips the re-render — leaving the flag stuck true and
      // making the next real user edit look like a sync, which would swallow
      // the edit.
      const nextUserInput = formatUTCDate(value);
      if (nextUserInput !== userInput) {
        isSyncingRef.current = true;
        setUserInput(nextUserInput);
      }
    }
    setChosenDate(incoming);
  }, [value]);

  useEffect(() => {
    onChange?.(chosenDate?.toISOString() || null);
  }, [chosenDate]);

  useEffect(() => {
    // Skip parsing if userInput was just updated by controlled value synchronization.
    // Only process userInput changes from actual user edits.
    if (isSyncingRef.current) {
      isSyncingRef.current = false;
      return;
    }

    if (userInput.length === 0) {
      setLookedMonth(now.getUTCMonth());
      setLookedYear(now.getUTCFullYear());
      setChosenDate(null);
      return;
    }
    const parsed = parseUserInputToUTC(userInput);
    if (parsed) {
      setLookedMonth(parsed.getUTCMonth());
      setLookedYear(parsed.getUTCFullYear());
      setChosenDate(parsed);
    }
  }, [userInput]);

  return {
    setUserInput,
    userInput,
    currentView,
    setCurrentView,
    chosenDate,
    UIYear: lookedYear ?? now.getUTCFullYear(),
    lookedMonth,
    lookedYear,
    now,
    setLookedMonth,
    setLookedYear,
    setChosenDate,
  };
};
